<!--
Copyright (c) 2020-2021 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <b-form>
        <fr-social-buttons v-if="!isSocialReg" :signin="false"></fr-social-buttons>
        <b-form-group class="mb-0 justify-content-center form-group" v-for="(property, key) in userDetails" :key="key">
            <fr-floating-label-input
                    v-if="property.type === 'string'"
                    :defaultValue="property.socialValue"
                    :fieldName="key"
                    :label="$t('pages.selfservice.registration.userDetails.' + key)"
                    :validateRules="calculateValidation(property)"
                    type="text"
                    v-model="saveDetails[key]"></fr-floating-label-input>

                <div v-else-if="property.type === 'boolean'" class="d-flex flex-column mb-4">
                    <label class="mr-auto" :for="property.title">{{property.title}}</label>

                    <div class="mr-auto">
                        <b-form-checkbox
                            switch
                            size="lg"
                            class="fr-toggle-primary"
                            v-model="saveDetails[key]" />
                    </div>
                </div>
            </b-form-group>

        <fr-policy-password-input v-if="!isSocialReg" policyApi="selfservice/registration" v-model="saveDetails.password" name="password" v-on:validate="onPasswordValidate"></fr-policy-password-input>
         <!-- Vue Bootstrap custom radio button seems to have problems so just using none component-->
        <div class="form-group mb-4">
            <div v-for="(preference, key) in userPreferences" :key="key" class="custom-control custom-checkbox mb-2">
                <input v-model="saveDetails.preferences[key]" type="checkbox" class="custom-control-input" :id="key">
                <label class="custom-control-label" :for="key">{{preference.description}}</label>
            </div>
        </div>
        <b-button v-if="inline === false" @click="save" :block="true" size="lg" variant="primary">
            {{$t("common.form.signUp")}}
        </b-button>
    </b-form>
</template>

<script>
import _ from 'lodash';
import FloatingLabelInput from '@/components/utils/FloatingLabelInput';
import PolicyPasswordInput from '@/components/utils/PolicyPasswordInput';
import SocialButtons from '@/components/utils/SocialButtons';
// import { uuid } from 'vue-uuid';
import { Validator } from 'vee-validate';

Validator.extend('notBlacklistedDomain', {
    getMessage: field => 'The maildomain in ' + field + ' is blacklisted.',
    validate (value, blacklist) {
        if (!_.isNil(value)) {
            let array = value.split('@');
            return blacklist.indexOf(array[1]) === -1;
        } else {
            return false;
        }
    }
});

/**
 * @description Selfservice stage for generating user details and displaying social buttons available. Works the same alone and in allinone
 *
 **/
export default {
    name: 'User-Details',
    components: {
        'fr-floating-label-input': FloatingLabelInput,
        'fr-policy-password-input': PolicyPasswordInput,
        'fr-social-buttons': SocialButtons
    },
    $_veeValidate: {
        validator: 'new'
    },
    props: {
        selfServiceDetails: { required: true },
        inline: {
            required: false,
            default: false
        }
    },
    mounted () {
        this.loadBlacklist();
    },
    data () {
        let data = {
            blacklist: {},
            userDetails: {},
            saveDetails: {},
            userPreferences: {},
            passwordValid: false,
            isSocialReg: _.get(this.selfServiceDetails, 'tag') !== 'initial'
        };

        if (this.selfServiceDetails.requirements && this.selfServiceDetails.requirements.registrationProperties) {
            data.userDetails = this.selfServiceDetails.requirements.registrationProperties.properties;

            if (_.has(this.selfServiceDetails, 'requirements.properties.user.default') &&
                    !_.isUndefined(this.selfServiceDetails, 'requirements.properties.user.default')) {
                _.each(this.selfServiceDetails.requirements.properties.user.default, (value, key) => {
                    data.userDetails[key].socialValue = value;
                });
            }

            _.each(this.selfServiceDetails.requirements.registrationProperties.required, (prop) => {
                data.userDetails[prop].required = true;
            });

            _.each(this.selfServiceDetails.requirements.registrationProperties.properties, (value, key) => {
                if (data.userDetails[key].type === 'boolean') {
                    data.saveDetails[key] = false;
                } else {
                    data.saveDetails[key] = '';
                }
            });
        }

        if (this.selfServiceDetails.requirements && this.selfServiceDetails.requirements.registrationPreferences) {
            data.saveDetails.preferences = {};
            data.userPreferences = this.selfServiceDetails.requirements.registrationPreferences;

            _.each(data.userPreferences, (value, key) => {
                data.saveDetails.preferences[key] = false;
            });
        }

        data.saveDetails.password = '';

        return data;
    },
    methods: {
        loadBlacklist () {
            const headers = this.getAnonymousHeaders();
            let selfServiceInstance = this.getRequestService({ headers });
            selfServiceInstance.get('/arena2036/maildomainBlacklist')
                .then(({ data }) => {
                    let firstline = true;
                    data.result.forEach(element => {
                        if (firstline) {
                            this.blacklist = element.name;
                            firstline = false;
                        } else {
                            this.blacklist += ',' + element.name;
                        }
                    });
                });
        },
        getData () {
            // lowercase email-address to prevent double-registration with different case settings
            this.saveDetails.mail = this.saveDetails.mail.toLowerCase().trim();
            this.saveDetails.preferences.primaryLanguage = this.$i18n.locale.substring(0, 2);
            // generate account-id
            // this.saveDetails._id = uuid.v4().toUpperCase();
            this.saveDetails.siamAccountId = this.saveDetails._id;
            let details = _.clone(this.saveDetails);

            delete details.confirmPassword;

            if (this.isSocialReg) {
                delete details.password;
            }

            return {
                user: details
            };
        },

        // Add additional frontend checks for field validation here
        calculateValidation (property) {
            let validators = [];
            if (property.required || property.title === 'First Name') {
                validators.push('required');
            };
            if (property.policies) {
                // Add policy vee validators correlations here
                _.each(property.policies, (policy) => {
                    if (policy.policyId === 'valid-email-address-format') {
                        validators.push('email');
                        validators.push('notBlacklistedDomain:' + this.blacklist);
                        validators.push('required');
                    }
                });
            }

            return validators.join('|');
        },

        save () {
            // Need to ignore this validation because it does not preform in testing due to vue validate
            /* istanbul ignore next */
            this.isValid().then((valid) => {
                console.log("pw Valid" + this.passwordValid);
                if (valid && this.passwordValid === true) {
                    this.$emit('advanceStage', this.getData());
                } else {
                    this.displayNotification('error', this.$t('pages.selfservice.registration.pleaseComplete'));
                }
            });
        },
        onPasswordValidate (valid) {
            this.passwordValid = valid;
            this.$emit('validate', valid);
        },

        isValid () {
            /* istanbul ignore next */
            return this.$validator.validateAll();
        }
    }
};
</script>
<style lang="scss">

.form-group ~ {
    width: 100%
}

fieldset > div{
    width: 100%
}
</style>
